/**
 * Permission helpers for the dashboard.
 *
 * Permissions are now DB-resolved by core and surfaced via session.perms and
 * session.is_tenant_admin / session.is_platform_admin (set from /api/dashboard/me).
 *
 * Usage:
 *   const { data: session } = useSession();
 *   if (hasPermission(session, "agents:edit")) { ... }
 *   if (isTenantAdmin(session)) { ... }
 */

import type { Session } from "next-auth";

/**
 * Check whether a session includes a permission key.
 * Platform admins always return true.
 */
export function hasPermission(
  session: Session | null | undefined,
  perm: string
): boolean {
  if (!session) return false;
  if (session.is_platform_admin) return true;
  const perms = session.perms ?? [];
  return perms.includes(perm);
}

/**
 * Check if the session has any of the supplied permission keys.
 * Platform admins always return true.
 */
export function hasAnyPermission(
  session: Session | null | undefined,
  ...perms: string[]
): boolean {
  if (!session) return false;
  if (session.is_platform_admin) return true;
  const sessionPerms = session.perms ?? [];
  return perms.some((p) => sessionPerms.includes(p));
}

/**
 * Check if the session has all of the supplied permission keys.
 * Platform admins always return true.
 */
export function hasAllPermissions(
  session: Session | null | undefined,
  ...perms: string[]
): boolean {
  if (!session) return false;
  if (session.is_platform_admin) return true;
  const sessionPerms = session.perms ?? [];
  return perms.every((p) => sessionPerms.includes(p));
}

/**
 * True when the caller is a tenant admin (group slug "admin") or platform admin.
 * Uses the server-resolved `is_tenant_admin` field from /api/dashboard/me.
 *
 * Note: being a tenant admin no longer implies `groups:manage`, `settings:read`, or
 * `settings:manage`; those keys may be stripped by the Tenant-Admin cap — gate UI with hasPermission.
 * Same for `settings_secondary:manage` (secondary tenant preferences).
 */
export function isTenantAdmin(session: Session | null | undefined): boolean {
  if (!session) return false;
  return session.is_tenant_admin === true || session.is_platform_admin === true;
}

/**
 * Check if the session has the platform:admin permission.
 * Uses the server-resolved `is_platform_admin` field.
 */
export function isPlatformAdmin(session: Session | null | undefined): boolean {
  if (!session) return false;
  return session.is_platform_admin === true;
}

/**
 * All permission keys used in the system.
 */
export const PERMISSIONS = {
  AGENTS_READ: "agents:read",
  AGENTS_EDIT: "agents:edit",
  DIDS_READ: "dids:read",
  DIDS_MANAGE: "dids:manage",
  CALLS_READ: "calls:read",
  CALLS_READ_ALL: "calls:read_all",
  TOOLS_MANAGE: "tools:manage",
  TENANTS_MANAGE: "tenants:manage",
  USERS_MANAGE: "users:manage",
  GROUPS_MANAGE: "groups:manage",
  AUDIT_READ: "audit:read",
  SETTINGS_READ: "settings:read",
  SETTINGS_MANAGE: "settings:manage",
  SETTINGS_SECONDARY_MANAGE: "settings_secondary:manage",
  PLATFORM_ADMIN: "platform:admin",
  AGENT_TEMPLATES_READ: "agent_templates:read",
  AGENT_TEMPLATES_MANAGE: "agent_templates:manage",
  EMAIL_TEMPLATES_MANAGE: "email_templates:manage",
} as const;
